import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

// React Bootstrap
import { Modal, Button } from 'react-bootstrap';

// Packages
import PropTypes from 'prop-types';
import Select from 'react-select';
import moment from 'moment-timezone';
import 'moment/locale/es';
import ClipLoader from 'react-spinners/ClipLoader';

const WeekSelectorModal = (props) => {
	const { show, onHide, cycle, report, onDownload, loading } = props;

	const { global_data } = useSelector(state => state.global);
	const { isAdminUser } = useSelector(state => state.auth);

	const [weeks, setWeeks] = useState([]);
	const [filteredWeeks, setFilteredWeeks] = useState([]);
	const [selectedWeek, setSelectedWeek] = useState(null);

	// Filter weeks based on Friday 6pm rule (only for non-admin users)
	useEffect(() => {
		if (isAdminUser) {
			setFilteredWeeks(weeks);
			return;
		}

		// Get current time in Mexico City timezone
		const now = moment().tz('America/Mexico_City');

		const availableWeeks = weeks.filter((week) => {
			const weekStart = moment(week.start).tz('America/Mexico_City');
			const weekEnd = moment(week.end).tz('America/Mexico_City');

			// If current date is within the week, it's available
			if (now.isBetween(weekStart, weekEnd, 'day', '[]')) {
				return true;
			}

			// If week has already passed, it's available
			if (now.isAfter(weekEnd)) {
				return true;
			}

			// For future weeks: check if it's Friday 6pm or later of the previous week
			// The week becomes available on Friday 6pm of the week before
			const fridayBefore = weekStart.clone().subtract(1, 'week').day(5).hour(18).minute(0).second(0);

			if (now.isSameOrAfter(fridayBefore)) {
				return true;
			}

			return false;
		});

		setFilteredWeeks(availableWeeks);
	}, [weeks, isAdminUser]);

	// Generate weeks from cycle start to end
	useEffect(() => {
		if (cycle?.start?.$date && cycle?.end?.$date) {
			moment.locale('es');
			const startDate = moment(cycle.start.$date).tz('America/Mexico_City');
			const endDate = moment(cycle.end.$date).tz('America/Mexico_City');

			const generatedWeeks = [];
			let currentDate = startDate.clone();
			let weekNumber = 1;

			while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
				// Find the end of the current week (Sunday)
				let weekEnd;
				const dayOfWeek = currentDate.day(); // 0 = Sunday, 1 = Monday, etc.

				if (dayOfWeek === 0) {
					// If it's Sunday, this day is the end of the week
					weekEnd = currentDate.clone();
				} else {
					// Find the next Sunday
					weekEnd = currentDate.clone().add(7 - dayOfWeek, 'day');
				}

				// Make sure week end doesn't exceed cycle end
				if (weekEnd.isAfter(endDate)) {
					weekEnd = endDate.clone();
				}

				// Capitalize first letter of day name
				const capitalizeFirst = (str) => str.charAt(0).toUpperCase() + str.slice(1);
				const startLabel = capitalizeFirst(currentDate.format('dddd D [de] MMMM'));
				const endLabel = capitalizeFirst(weekEnd.format('dddd D [de] MMMM'));

				generatedWeeks.push({
					value: weekNumber,
					label: `Semana ${weekNumber}: ${startLabel} - ${endLabel}`,
					start: currentDate.clone(),
					end: weekEnd.clone()
				});

				// Move to the next Monday
				currentDate = weekEnd.clone().add(1, 'day');
				weekNumber++;
			}

			setWeeks(generatedWeeks);
		}
	}, [cycle]);

	const handleDownload = () => {
		if (selectedWeek && report) {
			onDownload(report, selectedWeek);
			setSelectedWeek(null);
		}
	};

	const closeModal = () => {
		setSelectedWeek(null);
		onHide();
	};

	return (
		<Modal
			show={show}
			onHide={closeModal}
			backdrop='static'
			keyboard={false}
			size='md'
			aria-labelledby='week-selector-modal'
			centered
		>
			<Modal.Header closeButton>
				<Modal.Title>{report?.name || 'Seleccionar Semana'}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<p className='mb-3'>Selecciona la semana para generar el reporte:</p>
				<Select
					classNamePrefix='select'
					placeholder={global_data?.utils?.selectButtonPlaceholder || 'Seleccionar...'}
					noOptionsMessage={() => global_data?.utils?.selectButtonNoOptsMsg || 'Sin opciones'}
					maxMenuHeight={200}
					options={filteredWeeks}
					value={selectedWeek}
					onChange={(e) => setSelectedWeek(e)}
					isClearable={true}
					isSearchable={true}
				/>
			</Modal.Body>
			<Modal.Footer>
				<Button variant='outline-secondary' onClick={closeModal}>
					{global_data?.actionButtons?.cancel || 'Cancelar'}
				</Button>
				<Button
					className='submit-btn'
					onClick={handleDownload}
					disabled={!selectedWeek || loading}
				>
					{loading
						? <ClipLoader color='#ffffff' size={20} speedMultiplier={1} cssOverride={{ 'borderWidth': 'thick' }} />
						: <>{global_data?.actionButtons?.download || 'Descargar'} <i className='bi bi-download bi-bold-download ms-2'></i></>
					}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

WeekSelectorModal.propTypes = {
	show: PropTypes.bool.isRequired,
	onHide: PropTypes.func.isRequired,
	cycle: PropTypes.object.isRequired,
	report: PropTypes.object,
	onDownload: PropTypes.func.isRequired,
	loading: PropTypes.bool
};

export default WeekSelectorModal;
